<script setup lang="ts">
// One card of the morning ritual: a real deck card (a sticker) hanging on two threads
// and swaying a little, face down. A tap (or Enter/Space — it's a real button) turns it
// over on its threads; under it, a cream name tag and the card's one line.
// SSR: starts face down; the sway is CSS (deterministic delays), off under reduced motion.
import { ref } from 'vue';
import { useStatic } from '@core/services';
import TarotCard from '@app/presentation/components/tarot/TarotCard.vue';

const props = defineProps<{
  imgKey: string;
  name: string;
  line: string;
  delay: number;
}>();

const { getStaticUrl } = useStatic();
// The shared card back (static host, as the in-app draw) and this card's face — the
// deck's own art, served same-origin by the site (public/cards/NN.webp).
const backUrl = getStaticUrl('images/moon_magic_tarot/back.jpg');
const cardUrl = `${import.meta.env.BASE_URL}cards/${props.imgKey}.webp`;

const cardRef = ref<InstanceType<typeof TarotCard> | null>(null);
// Pre-interaction state for SSR/hydration: card starts unflipped (showing back).
const flipped = ref(false);
const turn = () => {
  cardRef.value?.flip();
  flipped.value = !flipped.value;
};
</script>

<template>
  <div class="flex flex-col items-center">
    <div
      class="ritual-hang w-full"
      :style="{ animationDelay: `${-props.delay * 2.2}s`, animationDuration: `${5 + props.delay}s` }"
    >
      <span
        class="ritual-thread left-[24%]"
        aria-hidden="true"
      />
      <span
        class="ritual-thread right-[24%]"
        aria-hidden="true"
      />
      <button
        type="button"
        class="ritual-card block w-full"
        :aria-pressed="flipped"
        aria-label="Перевернуть карту"
        @click="turn"
      >
        <TarotCard
          ref="cardRef"
          class="w-full"
          :card-key="props.name"
          :image-url="cardUrl"
          :back-image-url="backUrl"
          loading="lazy"
          relative
        />
      </button>
    </div>
    <!-- Caption: the hint while face down, then the card's name + line (space reserved). -->
    <div
      class="mt-4 flex min-h-[64px] w-full flex-col items-center text-center"
      aria-live="polite"
    >
      <span
        v-if="!flipped"
        class="ritual-tag is-hint type-overline px-2 py-0.5 text-muted-foreground"
      >переверни</span>
      <template v-else>
        <span class="ritual-tag paper-tag type-label px-2.5 py-0.5">{{ props.name }}</span>
        <p class="mt-2 text-[12px] leading-snug text-muted-foreground">{{ props.line }}</p>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Hangs from above on two threads and sways a little (transform only). */
.ritual-hang {
  position: relative;
  --sticker-w: 3px;
  transform-origin: 50% -40px;
  animation: ritual-sway 5.5s ease-in-out infinite alternate;
}
.ritual-thread {
  position: absolute;
  top: -40px;
  width: 1px;
  height: 44px;
  background: linear-gradient(transparent, color-mix(in srgb, var(--foreground) 42%, transparent));
}
.ritual-card:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 5px;
  border-radius: 6px;
}
.ritual-tag {
  border-radius: var(--paper-radius-xs);
  transform: rotate(-2deg);
}
.ritual-tag.paper-tag {
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 3px 6px -2px var(--paper-shade);
}
.ritual-tag.is-hint {
  border: 1.5px dashed color-mix(in srgb, var(--foreground) 30%, transparent);
}
@keyframes ritual-sway {
  from { transform: rotate(-1.4deg); }
  to { transform: rotate(1.4deg); }
}
@media (prefers-reduced-motion: reduce) {
  .ritual-hang {
    animation: none;
  }
}
</style>
